import { Auction, AuctionLot } from '../types/auction.types';

const WEBHOOK_URL = 'https://us-central1-auctionringers-e6565.cloudfunctions.net/whitelabelbuddyImport';

export interface WebhookResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Sync complete auction to AuctionRinger
 */
export const syncAuctionCreated = async (auction: Auction): Promise<WebhookResponse> => {
  try {
    console.log('📤 Syncing auction to AuctionRinger:', auction.id);

    const payload = {
      action: 'auction.created',
      data: {
        id: auction.id,
        auctionHouseId: auction.auctionHouseId,
        auctionHouseName: auction.auctionHouseName,
        title: auction.title,
        description: auction.description,
        startDate: auction.startDate.toISOString(),
        endDate: auction.endDate.toISOString(),
        location: auction.location,
        status: auction.status,
        visibility: auction.visibility,
        allowCrossPlatformPredictions: auction.allowCrossPlatformPredictions,
        lots: auction.lots.map(convertLotForWebhook),
      },
    };

    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Webhook failed:', errorText);
      return { success: false, error: errorText };
    }

    const result = await response.json();
    console.log('✅ Auction synced successfully:', result);
    return { success: true, message: result.message };

  } catch (error) {
    console.error('❌ Webhook error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Sync lot update (bid placed)
 */
export const syncLotUpdated = async (
  auctionId: string,
  lotId: string,
  updates: { currentBid: number; bidCount: number; status: string }
): Promise<WebhookResponse> => {
  try {
    const payload = {
      action: 'lot.updated',
      data: {
        auctionId,
        lotId,
        currentBid: updates.currentBid,
        bidCount: updates.bidCount,
        status: updates.status,
      },
    };

    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    return response.ok 
      ? { success: true } 
      : { success: false, error: await response.text() };

  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Sync auction ended with final results
 */
export const syncAuctionEnded = async (
  auctionId: string,
  results: Array<{ lotId: string; finalPrice: number; status: string }>
): Promise<WebhookResponse> => {
  try {
    const payload = {
      action: 'auction.ended',
      data: {
        auctionId,
        results,
      },
    };

    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    return response.ok 
      ? { success: true } 
      : { success: false, error: await response.text() };

  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Convert lot to webhook format
 */
const convertLotForWebhook = (lot: AuctionLot) => ({
  id: lot.id,
  sku: lot.sku,
  title: lot.title,
  description: lot.description,
  startingBid: lot.startingBid,
  reservePrice: lot.reservePrice,
  estimatedValue: lot.estimatedValue,
  expertLowEstimate: lot.expertLowEstimate,
  expertHighEstimate: lot.expertHighEstimate,
  category: lot.category,
  condition: lot.condition,
  year: lot.year,
  manufacturer: lot.manufacturer,
  images: lot.images,
  currentBid: lot.currentBid || lot.startingBid,
  bidCount: lot.bidCount,
  status: lot.status,
});

/**
 * Retry logic for failed webhooks
 */
export const retryWebhook = async (
  webhookFn: () => Promise<WebhookResponse>,
  maxRetries: number = 3
): Promise<WebhookResponse> => {
  const delays = [5000, 30000, 120000]; // 5s, 30s, 2m
  
  for (let i = 0; i < maxRetries; i++) {
    const result = await webhookFn();
    
    if (result.success) {
      return result;
    }
    
    if (i < maxRetries - 1) {
      console.log(`⏳ Retry ${i + 1}/${maxRetries} in ${delays[i] / 1000}s...`);
      await new Promise(resolve => setTimeout(resolve, delays[i]));
    }
  }
  
  return { success: false, error: 'Max retries exceeded' };
};